import { Controller } from "@hotwired/stimulus"

let sequence = 0

// Base for the Popper-positioned Flowbite components (dropdown, tooltip,
// popover). The controller sits on the trigger element and wraps the
// component bound to it:
//
//   <button data-controller="tooltip" data-tooltip-id-value="hint">…</button>
//   <div id="hint">…</div>
//
// Each trigger gets its own instance — a sequence-suffixed id with
// override: false — so several triggers may share one target element without
// destroying each other's bindings. If the target isn't in the DOM yet when
// the trigger connects (lazy frame, stream), the instance is built on the
// first interaction instead.
//
// Subclasses declare `triggerType` (with their own default) in static values
// and implement _create(target, instanceId) returning the Flowbite instance.
export default class extends Controller {
  static values = { id: String }

  connect() {
    this._lazyBuild = this._lazyBuild.bind(this)
    if (!this._build()) {
      this._fallbackEvents().forEach((event) =>
        this.element.addEventListener(event, this._lazyBuild),
      )
    }
  }

  disconnect() {
    this._unlisten()
    // destroy() leaves an open dropdown's click-outside listener on <body>;
    // hide() is what removes it.
    this.instance?.hide()
    this.instance?.destroyAndRemoveInstance()
    this.instance = null
  }

  // Runs on every interaction until the target exists and the instance is
  // built; only then does the fallback listener come off.
  _lazyBuild() {
    if (this._build()) {
      this._unlisten()
      this.instance.show()
    }
  }

  _unlisten() {
    this._fallbackEvents().forEach((event) =>
      this.element.removeEventListener(event, this._lazyBuild),
    )
  }

  // The same show events Flowbite binds for the trigger type, so a hover
  // never builds-and-shows a click-triggered component that the click would
  // then close.
  _fallbackEvents() {
    return this.triggerTypeValue === "click"
      ? ["click", "focus"]
      : ["mouseenter", "focus"]
  }

  _build() {
    if (this.instance) return true

    const target = document.getElementById(this.idValue)
    if (!target) return false

    this.instance = this._create(target, `${this.idValue}-${++sequence}`)
    return true
  }
}
